import { isEmpty } from 'ramda';
import {
  getYear,
  incDay,
  diffDay,
  getNextWeekDayFromDate,
  safeEncodeDate,
  tryEncodeDate,
  WeekDay,
  getYearMonthDay,
  sameDate,
  minDate,
  getWeekDay,
} from './dateUtils';

enum DayName {
  // Julserien
  daySfDoms,
  dayDoms,
  day1Adv,
  day2Adv,
  day3Adv,
  day4Adv,
  dayJuldagen,
  dayAnnandJul,
  daySeJul,
  dayNyarsdagen,
  daySeNyar,
  dayTrett,
  day1eTrett,
  day2eTrett,
  day3eTrett,
  day4eTrett,
  day5eTrett,
  day6eTrett,
  // Påskserien
  daySept,
  daySexa,
  dayFastl,
  dayFettisd,
  dayAskonsd,
  day1iFastan,
  day2iFastan,
  day3iFastan,
  day4iFastan,
  day5iFastan,
  dayMidfastos,
  dayPalms,
  daySkart,
  dayLangfr,
  dayPaskafton,
  dayPaskdagen,
  dayAnnandPask,
  day2iPask,
  day3iPask,
  day4iPask,
  day5iPask,
  dayBons,
  dayKristiHimmelsf,
  daySfPingst,
  dayPingstafton,
  dayPingstdagen,
  dayAnnandPingst,
  dayTref,
  day1eTref,
  day2eTref,
  day3eTref,
  day4eTref,
  day5eTref,
  day6eTref,
  day7eTref,
  day8eTref,
  day9eTref,
  day10eTref,
  day11eTref,
  day12eTref,
  day13eTref,
  day14eTref,
  day15eTref,
  day16eTref,
  day17eTref,
  day18eTref,
  day19eTref,
  day20eTref,
  day21eTref,
  day22eTref,
  day23eTref,
  day24eTref,
  day25eTref,
  dayApostlad,
  dayKristiForkl,
  // Övriga helgdagar
  dayKyndelsmass,
  dayMarieBeb,
  dayMidsommard,
  dayJohDopsd,
  dayMikaelsd,
  dayTacksagelsed,
  dayAllaHelgon,
  daySeAllaHelgon,
}

export enum ExtraDayName {
  // Borgerliga helgdagar
  xdayForstaMaj,
  xdayNationaldagen,
  // Övriga dagar
  xdayTrettonafton,
  xdayTjugondedagJul,
  xdayValborgafton,
  xdayMidsommarafton,
  xdayLucia,
  xdayJulafton,
  xdayVarnlosaBarnsd,
  xdayNyarsafton,
}

export enum DayEvent {
  // Händelser
  edayForintelsen,
  edayAllaHjartan,
  edayKvinnodagen,
  edayVaffeldagen,
  edaySommartidStart,
  edayEuropadagen,
  edayNorgeNatl,
  edayMorsd,
  edayDanmarkNatl,
  edaySverigeNatl,
  edayIslandNatl,
  edayValdagen,
  edayBarndagen,
  edayFNdagen,
  edaySommartidSlut,
  edayHalloween,
  edayGustavAdolf,
  edayFarsd,
  edayMartenAfton,
  edayMartenGas,
  edayFinlandNatl,
  edayNobeldagen,
  edayKungFodd,
  edayKungNamn,
  edayDrottningFodd,
  edayDrottningNamn,
  edayKronFodd,
  edayKronNamn,
}

const RED_DAYS: DayName[] = [
  DayName.dayJuldagen,
  DayName.dayAnnandJul,
  DayName.dayNyarsdagen,
  DayName.dayTrett,
  DayName.dayLangfr,
  DayName.dayPaskdagen,
  DayName.dayAnnandPask,
  DayName.dayKristiHimmelsf,
  DayName.dayPingstdagen,
  DayName.dayMidsommard,
  DayName.dayAllaHelgon,
];
const RED_XDAYS: ExtraDayName[] = [
  ExtraDayName.xdayForstaMaj,
  ExtraDayName.xdayNationaldagen,
];

const RED_DAYS_TOM_2004: DayName[] = [
  DayName.dayJuldagen,
  DayName.dayAnnandJul,
  DayName.dayNyarsdagen,
  DayName.dayTrett,
  DayName.dayLangfr,
  DayName.dayPaskdagen,
  DayName.dayAnnandPask,
  DayName.dayKristiHimmelsf,
  DayName.dayPingstdagen,
  DayName.dayAnnandPingst,
  DayName.dayMidsommard,
  DayName.dayAllaHelgon,
];

const RED_XDAYS_TOM_2004: ExtraDayName[] = [ExtraDayName.xdayForstaMaj];

// Fasta helgdagar, julserien (utgår från juldagen)
const DAYNAMES_JULHELGEN: Map<number, DayName> = new Map([
  [0, DayName.dayJuldagen],
  [1, DayName.dayAnnandJul],
  [7, DayName.dayNyarsdagen],
  [12, DayName.dayTrett],
]);

// Rörliga helgdagar, julserien (utgår från 4:e advent)
const DAYNAMES_JULSERIEN: Map<number, DayName> = new Map([
  [-35, DayName.daySfDoms],
  [-28, DayName.dayDoms],
  [-21, DayName.day1Adv],
  [-14, DayName.day2Adv],
  [-7, DayName.day3Adv],
  [0, DayName.day4Adv],
  [7, DayName.daySeJul],
  [14, DayName.daySeNyar],
]);

const DAYNAMES_PASKSERIEN: Map<number, DayName> = new Map([
  [-63, DayName.daySept],
  [-56, DayName.daySexa],
  [-49, DayName.dayFastl],
  [-47, DayName.dayFettisd],
  [-46, DayName.dayAskonsd],
  [-42, DayName.day1iFastan],
  [-35, DayName.day2iFastan],
  [-28, DayName.day3iFastan],
  [-21, DayName.dayMidfastos],
  [-14, DayName.day5iFastan],
  [-7, DayName.dayPalms],
  [-3, DayName.daySkart],
  [-2, DayName.dayLangfr],
  [-1, DayName.dayPaskafton],
  [0, DayName.dayPaskdagen],
  [1, DayName.dayAnnandPask],
  [7, DayName.day2iPask],
  [14, DayName.day3iPask],
  [21, DayName.day4iPask],
  [28, DayName.day5iPask],
  [35, DayName.dayBons],
  [39, DayName.dayKristiHimmelsf],
  [42, DayName.daySfPingst],
  [48, DayName.dayPingstafton],
  [49, DayName.dayPingstdagen],
  [50, DayName.dayAnnandPingst],
  [56, DayName.dayTref],
  [63, DayName.day1eTref],
  [70, DayName.day2eTref],
  [77, DayName.day3eTref],
  [84, DayName.day4eTref],
  [91, DayName.dayApostlad],
  [98, DayName.day6eTref],
  [105, DayName.dayKristiForkl],
  [112, DayName.day8eTref],
  [119, DayName.day9eTref],
  [126, DayName.day10eTref],
  [133, DayName.day11eTref],
  [140, DayName.day12eTref],
  [147, DayName.day13eTref],
  [154, DayName.day14eTref],
  [161, DayName.day15eTref],
  [168, DayName.day16eTref],
  [175, DayName.day17eTref],
  [182, DayName.day18eTref],
  [189, DayName.day19eTref],
  [196, DayName.day20eTref],
  [203, DayName.day21eTref],
  [210, DayName.day22eTref],
  [217, DayName.day23eTref],
  [224, DayName.day24eTref],
  [231, DayName.day25eTref],
]);
const DAYNAMES_TRETTONDESERIEN: Map<number, DayName> = new Map([
  [0, DayName.day1eTrett],
  [7, DayName.day2eTrett],
  [14, DayName.day3eTrett],
  [21, DayName.day4eTrett],
  [28, DayName.day5eTrett],
  [35, DayName.day6eTrett],
]);

const DAY_NAMES: Map<DayName, string> = new Map([
  [DayName.daySfDoms, 'söndagen före domssöndagen'],
  [DayName.dayDoms, 'domssöndagen'],
  [DayName.day1Adv, 'första söndagen i advent'],
  [DayName.day2Adv, 'andra söndagen i advent'],
  [DayName.day3Adv, 'tredje söndagen i advent'],
  [DayName.day4Adv, 'fjärde söndagen i advent'],
  [DayName.dayJuldagen, 'juldagen'],
  [DayName.dayAnnandJul, 'annandag jul'],
  [DayName.daySeJul, 'söndagen efter jul'],
  [DayName.dayNyarsdagen, 'nyårsdagen'],
  [DayName.daySeNyar, 'söndagen efter nyår'],
  [DayName.dayTrett, 'trettondedag jul'],
  [DayName.day1eTrett, 'första söndagen e. trettondedagen'],
  [DayName.day2eTrett, 'andra söndagen e. trettondedagen'],
  [DayName.day3eTrett, 'tredje söndagen e. trettondedagen'],
  [DayName.day4eTrett, 'fjärde söndagen e. trettondedagen'],
  [DayName.day5eTrett, 'femte söndagen e. trettondedagen'],
  [DayName.day6eTrett, 'sjätte söndagen e. trettondedagen'],
  [DayName.daySept, 'septuagesima'],
  [DayName.daySexa, 'sexagesima'],
  [DayName.dayFastl, 'fastlagssöndagen'],
  [DayName.dayFettisd, 'fettisdagen'],
  [DayName.dayAskonsd, 'askonsdagen'],
  [DayName.day1iFastan, 'första söndagen i fastan'],
  [DayName.day2iFastan, 'andra söndagen i fastan'],
  [DayName.day3iFastan, 'tredje söndagen i fastan'],
  [DayName.day4iFastan, 'fjärde söndagen i fastan'],
  [DayName.day5iFastan, 'femte söndagen i fastan'],
  [DayName.dayMidfastos, 'midfastosöndagen'],
  [DayName.dayPalms, 'palmsöndagen'],
  [DayName.daySkart, 'skärtorsdagen'],
  [DayName.dayLangfr, 'långfredagen'],
  [DayName.dayPaskafton, 'påskafton'],
  [DayName.dayPaskdagen, 'påskdagen'],
  [DayName.dayAnnandPask, 'annandag påsk'],
  [DayName.day2iPask, 'andra söndagen i påsktiden'],
  [DayName.day3iPask, 'tredje söndagen i påsktiden'],
  [DayName.day4iPask, 'fjärde söndagen i påsktiden'],
  [DayName.day5iPask, 'femte söndagen i påsktiden'],
  [DayName.dayBons, 'bönsöndagen'],
  [DayName.dayKristiHimmelsf, 'Kristi himmelsfärds dag'],
  [DayName.daySfPingst, 'söndagen före pingst'],
  [DayName.dayPingstafton, 'pingstafton'],
  [DayName.dayPingstdagen, 'pingstdagen'],
  [DayName.dayAnnandPingst, 'annandag pingst'],
  [DayName.dayTref, 'heliga trefaldighets dag'],
  [DayName.day1eTref, 'första söndagen e. trefaldighet'],
  [DayName.day2eTref, 'andra söndagen e. trefaldighet'],
  [DayName.day3eTref, 'tredje söndagen e. trefaldighet'],
  [DayName.day4eTref, 'fjärde söndagen e. trefaldighet'],
  [DayName.day5eTref, 'femte söndagen e. trefaldighet'],
  [DayName.day6eTref, 'sjätte söndagen e. trefaldighet'],
  [DayName.day7eTref, 'sjunde söndagen e. trefaldighet'],
  [DayName.day8eTref, 'åttonde söndagen e. trefaldighet'],
  [DayName.day9eTref, 'nionde söndagen e. trefaldighet'],
  [DayName.day10eTref, 'tionde söndagen e. trefaldighet'],
  [DayName.day11eTref, 'elfte söndagen e. trefaldighet'],
  [DayName.day12eTref, 'tolfte söndagen e. trefaldighet'],
  [DayName.day13eTref, 'trettonde söndagen e. trefaldighet'],
  [DayName.day14eTref, 'fjortonde söndagen e. trefaldighet'],
  [DayName.day15eTref, 'femtonde söndagen e. trefaldighet'],
  [DayName.day16eTref, 'sextonde söndagen e. trefaldighet'],
  [DayName.day17eTref, 'sjuttonde söndagen e. trefaldighet'],
  [DayName.day18eTref, 'artonde söndagen e. trefaldighet'],
  [DayName.day19eTref, 'nittonde söndagen e. trefaldighet'],
  [DayName.day20eTref, 'tjugonde söndagen e. trefaldighet'],
  [DayName.day21eTref, 'tjugoförsta söndagen e. trefaldighet'],
  [DayName.day22eTref, 'tjugoandra söndagen e. trefaldighet'],
  [DayName.day23eTref, 'tjugotredje söndagen e. trefaldighet'],
  [DayName.day24eTref, 'tjugofjärde söndagen e. trefaldighet'],
  [DayName.day25eTref, 'tjugofemte söndagen e. trefaldighet'],
  [DayName.dayApostlad, 'apostladagen'],
  [DayName.dayKristiForkl, 'Kristi förklarings dag'],
  [DayName.dayKyndelsmass, 'kyndelsmässodagen'],
  [DayName.dayMarieBeb, 'jungfru Marie bebådelsedag'],
  [DayName.dayMidsommard, 'midsommardagen'],
  [DayName.dayJohDopsd, 'den helige Johannes Döparens dag'],
  [DayName.dayMikaelsd, 'den helige Mikaels dag'],
  [DayName.dayTacksagelsed, 'tacksägelsedagen'],
  [DayName.dayAllaHelgon, 'alla helgons dag'],
  [DayName.daySeAllaHelgon, 'söndagen e. alla helgons dag'],
]);

const DAY_NAMES_SHORT: Map<DayName, string> = new Map([
  [DayName.daySfDoms, 'sönd f doms'],
  [DayName.dayDoms, 'domssöndagen'],
  [DayName.day1Adv, '1 i advent'],
  [DayName.day2Adv, '2 i advent'],
  [DayName.day3Adv, '3 i advent'],
  [DayName.day4Adv, '4 i advent'],
  [DayName.dayJuldagen, 'juldagen'],
  [DayName.dayAnnandJul, 'annandag jul'],
  [DayName.daySeJul, 'sönd e jul'],
  [DayName.dayNyarsdagen, 'nyårsdagen'],
  [DayName.daySeNyar, 'sönd e nyår'],
  [DayName.dayTrett, 'trettondedag jul'],
  [DayName.day1eTrett, '1 e trett'],
  [DayName.day2eTrett, '2 e trett'],
  [DayName.day3eTrett, '3 e trett'],
  [DayName.day4eTrett, '4 e trett'],
  [DayName.day5eTrett, '5 e trett'],
  [DayName.day6eTrett, '6 e trett'],
  [DayName.daySept, 'septuagesima'],
  [DayName.daySexa, 'sexagesima'],
  [DayName.dayFastl, 'fastlagssönd'],
  [DayName.dayFettisd, 'fettisdagen'],
  [DayName.dayAskonsd, 'askonsdagen'],
  [DayName.day1iFastan, '1 i fastan'],
  [DayName.day2iFastan, '2 i fastan'],
  [DayName.day3iFastan, '3 i fastan'],
  [DayName.day4iFastan, '4 i fastan'],
  [DayName.day5iFastan, '5 i fastan'],
  [DayName.dayMidfastos, 'midfastosönd'],
  [DayName.dayPalms, 'palmsöndag'],
  [DayName.daySkart, 'skärtorsdag'],
  [DayName.dayLangfr, 'långfredag'],
  [DayName.dayPaskafton, 'påskafton'],
  [DayName.dayPaskdagen, 'påskdagen'],
  [DayName.dayAnnandPask, 'annandag påsk'],
  [DayName.day2iPask, '2 i påsktiden'],
  [DayName.day3iPask, '3 i påsktiden'],
  [DayName.day4iPask, '4 i påsktiden'],
  [DayName.day5iPask, '5 i påsktiden'],
  [DayName.dayBons, 'bönsöndagen'],
  [DayName.dayKristiHimmelsf, 'Kristi himmelsfärd'],
  [DayName.daySfPingst, 'sönd f pingst'],
  [DayName.dayPingstafton, 'pingstafton'],
  [DayName.dayPingstdagen, 'pingstdagen'],
  [DayName.dayAnnandPingst, 'annandag pingst'],
  [DayName.dayTref, 'heliga trefaldighet'],
  [DayName.day1eTref, '1 e tref'],
  [DayName.day2eTref, '2 e tref'],
  [DayName.day3eTref, '3 e tref'],
  [DayName.day4eTref, '4 e tref'],
  [DayName.day5eTref, '5 e tref'],
  [DayName.day6eTref, '6 e tref'],
  [DayName.day7eTref, '7 e tref'],
  [DayName.day8eTref, '8 e tref'],
  [DayName.day9eTref, '9 e tref'],
  [DayName.day10eTref, '10 e tref'],
  [DayName.day11eTref, '11 e tref'],
  [DayName.day12eTref, '12 e tref'],
  [DayName.day13eTref, '13 e tref'],
  [DayName.day14eTref, '14 e tref'],
  [DayName.day15eTref, '15 e tref'],
  [DayName.day16eTref, '16 e tref'],
  [DayName.day17eTref, '17 e tref'],
  [DayName.day18eTref, '18 e tref'],
  [DayName.day19eTref, '19 e tref'],
  [DayName.day20eTref, '20 e tref'],
  [DayName.day21eTref, '21 e tref'],
  [DayName.day22eTref, '22 e tref'],
  [DayName.day23eTref, '23 e tref'],
  [DayName.day24eTref, '24 e tref'],
  [DayName.day25eTref, '25 e tref'],
  [DayName.dayApostlad, 'apostladagen'],
  [DayName.dayKristiForkl, 'Kristi förkl dag'],
  [DayName.dayKyndelsmass, 'kyndelsmäss'],
  [DayName.dayMarieBeb, 'jungfru Marie beb'],
  [DayName.dayMidsommard, 'midsommardagen'],
  [DayName.dayJohDopsd, 'Joh Döparens dag'],
  [DayName.dayMikaelsd, 'Mikaels dag'],
  [DayName.dayTacksagelsed, 'tacksägelsed'],
  [DayName.dayAllaHelgon, 'alla helgons dag'],
  [DayName.daySeAllaHelgon, 's e alla helgons d'],
]);

const XDAY_NAMES: Map<ExtraDayName, string> = new Map([
  [ExtraDayName.xdayForstaMaj, 'första maj'],
  [ExtraDayName.xdayNationaldagen, 'Sveriges nationaldag'],
  [ExtraDayName.xdayTrettonafton, 'trettondedagsafton'],
  [ExtraDayName.xdayTjugondedagJul, 'tjugondedag jul'],
  [ExtraDayName.xdayValborgafton, 'valborgsmässoafton'],
  [ExtraDayName.xdayMidsommarafton, 'midsommarafton'],
  [ExtraDayName.xdayLucia, 'Luciadagen'],
  [ExtraDayName.xdayJulafton, 'julafton'],
  [ExtraDayName.xdayVarnlosaBarnsd, 'värnlösa barns dag'],
  [ExtraDayName.xdayNyarsafton, 'nyårsafton'],
]);

const XDAY_NAMES_SHORT: Map<ExtraDayName, string> = new Map([
  [ExtraDayName.xdayForstaMaj, 'första maj'],
  [ExtraDayName.xdayNationaldagen, 'Sveriges nationaldag'],
  [ExtraDayName.xdayTrettonafton, 'trettonded.afton'],
  [ExtraDayName.xdayTjugondedagJul, 'tjugondedag jul'],
  [ExtraDayName.xdayValborgafton, 'valborgsm.afton'],
  [ExtraDayName.xdayMidsommarafton, 'midsommarafton'],
  [ExtraDayName.xdayLucia, 'Luciadagen'],
  [ExtraDayName.xdayJulafton, 'julafton'],
  [ExtraDayName.xdayVarnlosaBarnsd, 'värnlösa barns d'],
  [ExtraDayName.xdayNyarsafton, 'nyårsafton'],
]);

const DAYEVENT_NAMES: Map<DayEvent, string> = new Map([
  [DayEvent.edayForintelsen, 'förintelsens minnesdag'],
  [DayEvent.edayAllaHjartan, 'alla hjärtans dag'],
  [DayEvent.edayKvinnodagen, 'internationella kvinnodagen'],
  [DayEvent.edayVaffeldagen, 'våffeldagen'],
  [DayEvent.edaySommartidStart, 'sommartid börjar'],
  [DayEvent.edayEuropadagen, 'Europadagen'],
  [DayEvent.edayNorgeNatl, 'Norges nationaldag'],
  [DayEvent.edayMorsd, 'mors dag'],
  [DayEvent.edayDanmarkNatl, 'Danmarks grundlagsdag'],
  [DayEvent.edaySverigeNatl, 'Svenska flaggans dag'],
  [DayEvent.edayIslandNatl, 'Islands nationaldag'],
  [DayEvent.edayValdagen, 'valdagen'],
  [DayEvent.edayBarndagen, 'internationella barndagen'],
  [DayEvent.edayFNdagen, 'FN-dagen'],
  [DayEvent.edaySommartidSlut, 'sommartid slutar'],
  [DayEvent.edayHalloween, 'Halloween'],
  [DayEvent.edayGustavAdolf, 'Gustav Adolfsdagen'],
  [DayEvent.edayFarsd, 'fars dag'],
  [DayEvent.edayMartenAfton, 'Mårtensafton'],
  [DayEvent.edayMartenGas, 'Mårtensgås'],
  [DayEvent.edayFinlandNatl, 'Finlands självständighetsdag'],
  [DayEvent.edayNobeldagen, 'Nobeldagen'],
  [DayEvent.edayKungFodd, 'konungens födelsedag'],
  [DayEvent.edayKungNamn, 'konungens namnsdag'],
  [DayEvent.edayDrottningFodd, 'drottningens födelsedag'],
  [DayEvent.edayDrottningNamn, 'drottningens namnsdag'],
  [DayEvent.edayKronFodd, 'kronprinsessans födelsedag'],
  [DayEvent.edayKronNamn, 'kronprinsessans namnsdag'],
]);

const DAYEVENT_NAMES_SHORT: Map<DayEvent, string> = new Map([
  [DayEvent.edayForintelsen, 'förintelsens minnesd'],
  [DayEvent.edayAllaHjartan, 'alla hjärtans dag'],
  [DayEvent.edayKvinnodagen, 'intern.kvinnodagen'],
  [DayEvent.edayVaffeldagen, 'våffeldagen'],
  [DayEvent.edaySommartidStart, 'sommartid börjar'],
  [DayEvent.edayEuropadagen, 'Europadagen'],
  [DayEvent.edayNorgeNatl, 'Norges nationaldag'],
  [DayEvent.edayMorsd, 'mors dag'],
  [DayEvent.edayDanmarkNatl, 'Danmarks grundlagsdag'],
  [DayEvent.edaySverigeNatl, 'Svenska flaggans dag'],
  [DayEvent.edayIslandNatl, 'Islands nationaldag'],
  [DayEvent.edayValdagen, 'valdagen'],
  [DayEvent.edayBarndagen, 'intern.barndagen'],
  [DayEvent.edayFNdagen, 'FN-dagen'],
  [DayEvent.edaySommartidSlut, 'sommartid slutar'],
  [DayEvent.edayHalloween, 'Halloween'],
  [DayEvent.edayGustavAdolf, 'Gustav Adolfsdagen'],
  [DayEvent.edayFarsd, 'fars dag'],
  [DayEvent.edayMartenAfton, 'Mårtensafton'],
  [DayEvent.edayMartenGas, 'Mårtensgås'],
  [DayEvent.edayFinlandNatl, 'Finlands självständ'],
  [DayEvent.edayNobeldagen, 'Nobeldagen'],
  [DayEvent.edayKungFodd, 'konungens födelsed'],
  [DayEvent.edayKungNamn, 'konungens namnsd'],
  [DayEvent.edayDrottningFodd, 'drottningens födelsed'],
  [DayEvent.edayDrottningNamn, 'drottningens namnsd'],
  [DayEvent.edayKronFodd, 'kronprinsessans födelsed'],
  [DayEvent.edayKronNamn, 'kronprinsessans namnsd'],
]);

const OLD_DAY_NAMES: Map<DayName, string> = new Map([
  [DayName.day2iPask, 'första söndagen efter påsk'],
  [DayName.day3iPask, 'andra söndagen efter påsk'],
  [DayName.day4iPask, 'tredje söndagen efter påsk'],
  [DayName.day5iPask, 'fjärde söndagen efter påsk'],
  [DayName.daySfPingst, 'sjätte söndagen efter påsk'],
  [DayName.dayJohDopsd, ''],
]);

const OLD_DAY_NAMES_SHORT: Map<DayName, string> = new Map([
  [DayName.day2iPask, '1 e påsk'],
  [DayName.day3iPask, '2 e påsk'],
  [DayName.day4iPask, '3 e påsk'],
  [DayName.day5iPask, '4 e påsk'],
  [DayName.daySfPingst, '6 e påsk'],
  [DayName.dayJohDopsd, ''],
]);

export const easterDate = (AYear: number): Date => {
  let a: number, b: number, c: number, d: number, e: number;
  let f: number, g: number, h: number, i: number, k: number;
  let l: number, m: number;
  if (AYear >= 1583) {
    a = AYear % 19; //calculate the year's position in the Metonic cycle.
    b = Math.floor(AYear / 100); //split the year into centuries
    c = AYear % 100;
    d = Math.floor(b / 4); //leap years calculations?
    e = b % 4; //leap years calculations?
    f = Math.floor((b + 8) / 25);
    g = Math.floor((b - f + 1) / 3);
    h = (19 * a + b - d - g + 15) % 30;
    i = Math.floor(c / 4);
    k = c % 4;
    l = (32 + 2 * e + 2 * i - h - k) % 7;
    m = Math.floor((a + 11 * h + 22 * l) / 451);
    return (
      tryEncodeDate(
        AYear,
        Math.floor((h + l - 7 * m + 114) / 31),
        ((h + l - 7 * m + 114) % 31) + 1
      ) ?? safeEncodeDate(AYear, 1, 1)
    );
  } else {
    a = AYear % 19;
    b = AYear % 4;
    c = AYear % 7;
    d = (19 * a + 15) % 30;
    e = (2 * b + 4 * c + 6 * d + 6) % 7;
    f = 22 + d + e;
    // { Undantag:
    //   Om resultatet blir 26:e April (f = 57) eller
    //   om resultatet blir 25:e April (f = 56) och d = 28, e = 6 och a > 10
    //   så dras 7 dagar bort. }
    if (f === 57 || (f === 56 && d === 28 && e === 6 && a > 10)) {
      f = f - 7;
    }
    if (f <= 31) {
      return tryEncodeDate(AYear, 3, f) ?? safeEncodeDate(AYear, 1, 1);
    } else
      return tryEncodeDate(AYear, 4, f - 31) ?? safeEncodeDate(AYear, 1, 1);
  }
};

// Röda dagar
export const isRedDay = (date: Date): boolean => {
  const year = getYear(date);
  const dayName = getDayName(date);
  const xdayName = getExtraDayName(date);
  const weekDay = getWeekDay(date);
  if (year >= 2005) {
    return (
      weekDay === WeekDay.Sunday ||
      (dayName !== undefined && RED_DAYS.includes(dayName)) ||
      (xdayName !== undefined && RED_XDAYS.includes(xdayName))
    );
  } else
    return (
      weekDay === WeekDay.Sunday ||
      (dayName !== undefined && RED_DAYS_TOM_2004.includes(dayName)) ||
      (xdayName !== undefined && RED_XDAYS_TOM_2004.includes(xdayName))
    );
};

export const getDayName = (date: Date): DayName | undefined => {
  const dayName_minAr = 1983;
  const year = getYear(date);
  let result = undefined;
  if (year >= dayName_minAr) {
    const easterDay = easterDate(year);

    if (result === undefined) {
      // Testa julhelgen (utgår från juldagen)
      const julDagen = safeEncodeDate(year, 12, 25);
      const julHelgen = DAYNAMES_JULHELGEN.get(diffDay(julDagen, date));
      if (julHelgen !== undefined) {
        result = julHelgen;
      }
    }
    if (result === undefined) {
      // Testa julserien (utgår från 4:e advent, som är söndagen i intervallet 18-24/12)
      const fourthAdvent = getNextWeekDayFromDate(
        safeEncodeDate(year, 12, 18),
        WeekDay.Sunday
      );
      const julSerien = DAYNAMES_JULSERIEN.get(diffDay(fourthAdvent, date));
      if (julSerien !== undefined) {
        result = julSerien;
      }
    }
    if (result === undefined) {
      // Testa påskserien (utgår från påskdagen)
      const paskSerien = DAYNAMES_PASKSERIEN.get(diffDay(easterDay, date));
      if (paskSerien !== undefined) {
        result = paskSerien;
      }
    }

    if (result === undefined) {
      // Testa trettondeserien (utgår från söndagen efter trettondedagen, som är första söndagen fr.o.m. 7/1)
      const efterTr = getNextWeekDayFromDate(
        safeEncodeDate(year, 1, 7),
        WeekDay.Sunday
      );
      const trettSerien = DAYNAMES_TRETTONDESERIEN.get(diffDay(efterTr, date));
      if (trettSerien !== undefined) {
        result = trettSerien;
      }
    }
    if (result === undefined) {
      // Testa förra årets julhelg
      const julDagen = safeEncodeDate(year - 1, 12, 25);
      const julHelgen = DAYNAMES_JULHELGEN.get(diffDay(julDagen, date));
      if (julHelgen !== undefined) {
        result = julHelgen;
      }
    }
    if (result === undefined) {
      // Testa förra årets julserie
      const fourthAdvent = getNextWeekDayFromDate(
        safeEncodeDate(year - 1, 12, 18),
        WeekDay.Sunday
      );
      const julSerien = DAYNAMES_JULSERIEN.get(diffDay(fourthAdvent, date));
      if (julSerien !== undefined) {
        result = julSerien;
      }
    }

    // Övriga helgdagar (överstyr namnen från serierna)

    // Kyndelsmässodagen
    // Söndagen i intervallet 2-8/2, dock senast söndagen före fastlagssöndagen (sexagesima, 56 dagar före påsk)
    if (
      sameDate(
        date,
        minDate(
          getNextWeekDayFromDate(safeEncodeDate(year, 2, 2), WeekDay.Sunday),
          incDay(easterDay, -56)
        )
      )
    ) {
      result = DayName.dayKyndelsmass;
    }

    // Jungfru Marie bebådelsedag
    // Söndagen i intervallet 22-28/3, dock senast femte söndagen i fastan (14 dagar före påsk)
    if (
      sameDate(
        date,
        minDate(
          getNextWeekDayFromDate(safeEncodeDate(year, 3, 22), WeekDay.Sunday),
          incDay(easterDay, -14)
        )
      )
    ) {
      result = DayName.dayMarieBeb;
    }

    // Midsommardagen och den helige Johannes döparens dag
    // Lördagen i intervallet 20-26/6, samt dagen efter
    const midsommar = getNextWeekDayFromDate(
      safeEncodeDate(year, 6, 20),
      WeekDay.Saturday
    );
    if (sameDate(date, midsommar)) {
      result = DayName.dayMidsommard;
    }
    if (sameDate(date, incDay(midsommar, 1))) {
      result = DayName.dayJohDopsd;
    }

    // Den helige Mikaels dag
    // Söndagen i intervallet 29/9 - 5/10
    if (
      sameDate(
        date,
        getNextWeekDayFromDate(safeEncodeDate(year, 9, 29), WeekDay.Sunday)
      )
    ) {
      result = DayName.dayMikaelsd;
    }

    // Tacksägelsedagen
    // Andra söndagen i oktober (söndagen i intervallet 8-14/10).
    if (
      sameDate(
        date,
        getNextWeekDayFromDate(safeEncodeDate(year, 10, 8), WeekDay.Sunday)
      )
    ) {
      result = DayName.dayTacksagelsed;
    }

    // Alla helgons dag och söndagen efter alla helgons dag
    // Löndagen i intervallet 31/10-6/11, samt dagen efter
    const allaHelgon = getNextWeekDayFromDate(
      safeEncodeDate(year, 10, 31),
      WeekDay.Saturday
    );
    if (sameDate(date, allaHelgon)) {
      result = DayName.dayAllaHelgon;
    }
    if (sameDate(date, incDay(allaHelgon, 1))) {
      result = DayName.daySeAllaHelgon;
    }
  }
  return result;
};

export const getExtraDayName = (date: Date): ExtraDayName | undefined => {
  let result = undefined;
  const [year, month, day] = getYearMonthDay(date);
  // Borgerliga helgdagar (överstyr INTE övriga dagar)

  // Första maj
  if (sameDate(date, safeEncodeDate(year, 5, 1))) {
    result = ExtraDayName.xdayForstaMaj;
  }
  // Sveriges nationaldag
  if (sameDate(date, safeEncodeDate(year, 6, 6))) {
    result = ExtraDayName.xdayNationaldagen;
  }

  // Övriga dagar

  // Trettondedagsafton
  // 5/1
  if (month === 1 && day === 5) {
    result = ExtraDayName.xdayTrettonafton;
  }
  // Tjugondedag jul
  // 13/1
  if (month === 1 && day === 13) {
    result = ExtraDayName.xdayTjugondedagJul;
  }
  // Valborgsmässoafton
  // 30/4
  if (month === 4 && day === 30) {
    result = ExtraDayName.xdayValborgafton;
  }
  // Midsommarafton
  // Fredagen i intervallet 19-25/6
  const midsommarAfton = getNextWeekDayFromDate(
    safeEncodeDate(year, 6, 19),
    WeekDay.Friday
  );
  if (sameDate(date, midsommarAfton)) {
    result = ExtraDayName.xdayMidsommarafton;
  }
  // Lucia
  // 13/12
  if (month === 12 && day === 13) {
    result = ExtraDayName.xdayLucia;
  }
  // Julafton
  // 24/12
  if (month === 12 && day === 24) {
    result = ExtraDayName.xdayJulafton;
  }
  // Värnlösa barns dag
  // 28/12
  if (month === 12 && day === 28) {
    result = ExtraDayName.xdayVarnlosaBarnsd;
  }
  // Nyårsafton
  // 31/12
  if (month === 12 && day === 31) {
    result = ExtraDayName.xdayNyarsafton;
  }
  return result;
};

export const getDayEvents = (date: Date): DayEvent[] | undefined => {
  let result: DayEvent[] = [];
  const [year, month, day] = getYearMonthDay(date);
  // Förintelsens minnesdag
  // 27/1
  if (month === 1 && day === 27) {
    result.push(DayEvent.edayForintelsen);
  }
  // Alla hjärtans dag
  // 14/2
  if (month === 2 && day === 14) {
    result.push(DayEvent.edayAllaHjartan);
  }
  // Internationella kvinnodagen
  // 8/3
  if (month === 3 && day === 8) {
    result.push(DayEvent.edayKvinnodagen);
  }
  // Våffeldagen
  // 25/3
  if (month === 3 && day === 25) {
    result.push(DayEvent.edayVaffeldagen);
  }
  // Sommartid börjar
  // Sista söndagen i mars (söndagen i intervallet 25-31/3).
  if (
    sameDate(
      date,
      getNextWeekDayFromDate(safeEncodeDate(year, 3, 25), WeekDay.Sunday)
    )
  ) {
    result.push(DayEvent.edaySommartidStart);
  }
  // Europadagen
  // 9/5
  if (month === 5 && day === 9) {
    result.push(DayEvent.edayEuropadagen);
  }
  // Norges nationaldag
  // 17/5
  if (month === 5 && day === 17) {
    result.push(DayEvent.edayNorgeNatl);
  }
  // Mors dag
  // Sista söndagen i maj (söndagen i intervallet 25-31/5).
  if (
    sameDate(
      date,
      getNextWeekDayFromDate(safeEncodeDate(year, 5, 25), WeekDay.Sunday)
    )
  ) {
    result.push(DayEvent.edayMorsd);
  }
  // Danmarks grundlagsdag
  // 5/6
  if (month === 6 && day === 5) {
    result.push(DayEvent.edayDanmarkNatl);
  }
  // Sveriges nationaldag och Svenska flaggans dag
  // 6/6
  if (month === 6 && day === 6) {
    result.push(DayEvent.edaySverigeNatl);
  }
  // Islands nationaldag
  // 17/6
  if (month === 6 && day === 17) {
    result.push(DayEvent.edayIslandNatl);
  }
  // Valdagen
  // Tredje söndagen i september (söndagen i intervallet 15-21/9) vart fjärde år (år delbara med 2 men ej 4).
  if (
    year % 4 === 2 &&
    sameDate(
      date,
      getNextWeekDayFromDate(safeEncodeDate(year, 9, 15), WeekDay.Sunday)
    )
  ) {
    result.push(DayEvent.edayValdagen);
  }
  // Internationella barndagen
  // 4/10
  if (month === 10 && day === 4) {
    result.push(DayEvent.edayBarndagen);
  }
  // FN-dagen
  // 24/10
  if (month === 10 && day === 24) {
    result.push(DayEvent.edayFNdagen);
  }
  // Sommartid slutar
  // Sista söndagen i oktober (söndagen i intervallet 25-31/10).
  if (
    sameDate(
      date,
      getNextWeekDayFromDate(safeEncodeDate(year, 10, 25), WeekDay.Sunday)
    )
  ) {
    result.push(DayEvent.edaySommartidSlut);
  }
  // Halloween
  // 31/10
  if (month === 10 && day === 31) {
    result.push(DayEvent.edayHalloween);
  }
  // Gustav Adolfsdagen
  // 6/11
  if (month === 11 && day === 6) {
    result.push(DayEvent.edayGustavAdolf);
  }
  // Fars dag
  // Andra söndagen i november (söndagen i intervallet 8-14/11).
  if (
    sameDate(
      date,
      getNextWeekDayFromDate(safeEncodeDate(year, 11, 8), WeekDay.Sunday)
    )
  ) {
    result.push(DayEvent.edayFarsd);
  }
  // Mårtensafton och Mårtensgås
  // 10-11/11
  if (month === 11 && day === 10) {
    result.push(DayEvent.edayMartenAfton);
  }
  if (month === 11 && day === 11) {
    result.push(DayEvent.edayMartenGas);
  }
  // Finlands självständighetsdag
  // 6/12
  if (month === 12 && day === 6) {
    result.push(DayEvent.edayFinlandNatl);
  }
  // Nobeldagen
  // 10/12
  if (month === 12 && day === 10) {
    result.push(DayEvent.edayNobeldagen);
  }

  // Konungens födelsedag
  // 30/4
  if (month === 4 && day === 30) {
    result.push(DayEvent.edayKungFodd);
  }
  // Konungens namnsdag
  // 28/1
  if (month === 1 && day === 28) {
    result.push(DayEvent.edayKungNamn);
  }
  // Drottningens födelsedag
  // 23/12
  if (month === 12 && day === 23) {
    result.push(DayEvent.edayDrottningFodd);
  }
  // Drottningens namnsdag
  // 8/8
  if (month === 8 && day === 8) {
    result.push(DayEvent.edayDrottningNamn);
  }
  // Kronprinsessans födelsedag
  // 14/7
  if (month === 7 && day === 14) {
    result.push(DayEvent.edayKronFodd);
  }
  // Kronprinsessans namnsdag
  // 12/3
  if (month === 3 && day === 12) {
    result.push(DayEvent.edayKronNamn);
  }

  return isEmpty(result) ? undefined : result;
};

export const getDayNameText = (date: Date): string => {
  const dayName = getDayName(date);
  if (dayName !== undefined) {
    if (getYear(date) < 2004) {
      const oldName = OLD_DAY_NAMES.get(dayName);
      if (oldName !== undefined) {
        return oldName;
      }
    }
    return DAY_NAMES.get(dayName) ?? '';
  }
  return '';
};

export const getDayNameShortText = (date: Date): string => {
  const dayName = getDayName(date);
  if (dayName !== undefined) {
    if (getYear(date) < 2004) {
      const oldName = OLD_DAY_NAMES_SHORT.get(dayName);
      if (oldName !== undefined) {
        return oldName;
      }
    }
    return DAY_NAMES_SHORT.get(dayName) ?? '';
  }
  return '';
};

export const getExtraDayNameText = (date: Date): string => {
  const dayName = getExtraDayName(date);
  return dayName !== undefined ? XDAY_NAMES.get(dayName) ?? '' : '';
};

export const getExtraDayNameShortText = (date: Date): string => {
  const dayName = getExtraDayName(date);
  return dayName !== undefined ? XDAY_NAMES_SHORT.get(dayName) ?? '' : '';
};

export const getDayEventTexts = (date: Date): string[] | undefined => {
  const dayEvents = getDayEvents(date)
    ?.map(dayEvent => DAYEVENT_NAMES.get(dayEvent) ?? '')
    .filter(name => name !== '');
  return dayEvents !== undefined && dayEvents.length > 0
    ? dayEvents
    : undefined;
};

export const getDayEventShortTexts = (date: Date): string[] | undefined => {
  const dayEvents = getDayEvents(date)
    ?.map(dayEvent => DAYEVENT_NAMES_SHORT.get(dayEvent) ?? '')
    .filter(name => name !== '');
  return dayEvents !== undefined && dayEvents.length > 0
    ? dayEvents
    : undefined;
};

export const getAllDayNameTexts = (
  date: Date,
  includeEvents: boolean = false
): string[] | undefined => {
  const dayNames = [getDayNameText(date), getExtraDayNameText(date)]
    .concat(includeEvents ? getDayEventTexts(date) ?? [] : [])
    .filter(name => name !== '');
  return dayNames.length > 0 ? dayNames : undefined;
};

export const getAllDayNameShortTexts = (
  date: Date,
  includeEvents: boolean = false
): string[] | undefined => {
  const dayNames = [getDayNameShortText(date), getExtraDayNameShortText(date)]
    .concat(includeEvents ? getDayEventShortTexts(date) ?? [] : [])
    .filter(name => name !== '');
  return dayNames.length > 0 ? dayNames : undefined;
};

export const getOneDayNameText = (date: Date): string => {
  const dayName = getDayNameText(date);
  if (dayName !== '') {
    return dayName;
  } else {
    return getExtraDayNameText(date);
  }
};

export const getOneDayNameShortText = (date: Date): string => {
  const dayName = getDayNameShortText(date);
  if (dayName !== '') {
    return dayName;
  } else {
    return getExtraDayNameShortText(date);
  }
};
